/** @format */

import { Action } from '@ngrx/store';
import { HttpErrorResponse } from '@angular/common/http';
import { SearchItem, SearchItemType, DnvLatLng, DnvMapInfo } from 'dnv-lib';
import { Problem, ProblemContact, ContactDB, PhoneType } from '../../models/problem';
import { Activity, ActivityList, TaskList } from '../../models/activity';
import { NgxGalleryOptions, NgxGalleryAnimation, NgxGalleryImageSize, NgxGalleryImage } from '@vinlos/ngx-gallery';
import { History } from 'src/app/models/history';
import { Attachment } from 'src/app/models/attachment';

export function prop<T, K extends keyof T>(obj: T, key: K) {
	return obj[key];
}

// export type sideNavItems = 'overview' | 'activity-log' | 'documents' | 'history';
export type DashboardView = 'list' | 'map' | 'maplist';

export type DashboardTabView = 'top' | 'overview' | 'activity-log' | 'documents' | 'history' | 'contacts' | 'linked-cases' | 'assets';
export function isDashboardTabView(d: string | DashboardTabView): boolean {
	return ['top', 'overview', 'activity-log', 'documents', 'history'].indexOf(d) > -1;
}

export interface Asset {
	asset_Id: number;
	problem_RefId: number;
	gisAssetId: string;
	featureLayer_UId: string;
}

export interface AssetPayload {
	caseNo: string;
	asset: Asset;
	success?: Asset;
	error?: HttpErrorResponse;
}

export interface ProblemType {
	problemType_Id: number;
	problemType_Name: string;
	description: string;
	active: boolean;
}

export interface SelectedProblemSubType {
	problemSubType_Id: number;
	problemSubType_Name: string;
	description: string;
	active: boolean;
	problemType_RefId: number;
}

export interface ProblemPriority {
	problemPriority_Id: number;
	priority: string;
}

export interface ProblemMenuConfig {
	problemMenuConfig_Id: number;
	problemType_RefId: number;
	group_RefId: number;
	menuOne: string;
	menuTwo: string;
	menuThree: string;
	message: string;
	active: boolean;
	showMenu: boolean;
	isTWNation?: boolean;
	isSNation?: boolean;
}

export interface ProblemCategorySubType {
	problemCategory_Name: string;
	problemCategory_Subtype: ProblemSubType[];
	isExpanded: boolean;
}

export interface ProblemSubType {
	problemSubType_Id?: number;
	problemType_Id?: number;
	problemSubType_Name?: string;
	problemType_Name?: string;
}

export interface ReassignProblemPayload {
	caseNo: string;
	problem_Id: number;
	user_RefId: number | null;
	role_RefId: number | null;
	group_RefId: number;
	problemType_RefId: number | null;
	notes: string;
	origNote: string;
	assignToMe?: boolean;
	success?: ActivityList;
	error?: string;
}

/* export interface ReassignGroup {
  caseNo: string;
  problemType_RefId: number;
  notes: string;
  origNote: string;
  user_refId?: number;
  role_refId?: number;
  group_refId: number;
  success?: ActivityList;
  error?: string;
} */

export interface CancelActivityPayload {
	activityId: number;
	activityNo: string;
	problemNo: string;
	success?: ActivityList[];
	error?: HttpErrorResponse;
}

export interface UpdateActivityDetails {
	activityId: number;
	activity: Activity;
	activityNo?: string;
	success?: ActivityList[];
	error?: HttpErrorResponse;
}

export interface LocationCoordinate {
	ltopid: string;
	dblink: string;
	lat: number;
	lng: number;
}

export interface SearchLocation {
	locationType: string;
	location: string;
}

export interface SearchFromCoordinatesPayload {
	problemMenu?: ProblemMenuConfig;
	dnvMapInfo: DnvMapInfo;
	zoneLookupOnly: boolean;
	updatedBy?: string;
	success?: SearchCoordinatesPayload;
	error?: HttpErrorResponse;
}

// Based on dnvlib SearchItem, SearchCoordinatesPayload is a SearchItem
export interface SearchCoordinatesPayload {
	id: number;
	name: string;
	type: SearchItemType;
	problemMenu?: ProblemMenuConfig;
	searchLocation?: SearchLocation[];
	assetId?: string;
	updatedBy?: string;
	success?: LocationCoordinate;
	error?: HttpErrorResponse;
}

export interface SearchProblem {
	problemId: number;
	caseNumber: string;
	groupId: number;
	typeId: number;
	location: string;
	sourceId: number;
	statusId: number;
	priorityId: number;
	assigneeId: number;
	roleId: number;
	description: string;
	reportedDate: string;
	completedDate?: string;
	reporterFirstName: string;
	reporterLastName: string;
	location_Longitude: number;
	location_Latitude: number;
	status?: string;
	typeName?: string;
}

export interface SearchProblemParsed {
	problemId: number;
	caseNumber: string;
	groupName: string;
	typeId: number;
	typeName: string;
	location: string;
	source: string;
	status: string;
	assignee: string;
	description: string;
	reportedDate: string;
	completedDate?: string;
	reporter: string;
}

export interface SearchQuery {
	savedQuery_Id: number;
	savedQueryName: string;
	caseNumber?: string;
	description?: string;
	typeId?: string;
	subTypeId?: string;
	statusId?: string;
	sourceId?: string;
	groupId?: string;
	assigneeId?: string;
	contactId?: string;
	roleId?: string;
	reportedDateBegin?: string;
	reportedDateEnd?: string;
	completedDateBegin?: string;
	completedDateEnd?: string;
	polygon?: [[number, number]];
	priorityId?: string;
	workOrderNumber?: string;
	location?: string;
	firstName?: string;
	lastName?: string;
	address?: string;
	phone?: string;
	email?: string;
}

export type SearchResultsPayloadTypes = keyof SearchResultsPayload;

export const queryParameterNames: SearchResultsPayloadTypes[] = [
	'caseNumber',
	'description',
	'typeId',
	'subTypeId',
	'statusId',
	'sourceId',
	'receivedFromId',
	'groupId',
	'assigneeId',
	'contactId',
	'roleId',
	'reportedDateBegin',
	'reportedDateEnd',
	'completedDateBegin',
	'completedDateEnd',
	'location',
	'firstName',
	'lastName',
	'address',
	'phone',
	'email',
	'priorityId',
	'workOrderNumber',
];

export interface SearchResultsPayload {
	// Search by Problem

	// Details
	caseNumber?: string;
	description?: string;
	typeId?: string;
	subTypeId?: string;
	statusId?: string;
	sourceId?: string;
	receivedFromId?: string;
	groupId?: string;
	assigneeId?: string;
	contactId?: string;
	roleId?: string;
	reportedDateBegin?: string;
	reportedDateEnd?: string;
	completedDateBegin?: string;
	completedDateEnd?: string;
	polygon?: [[number, number]];
	priorityId?: string;
	workOrderNumber?: string;

	// Location
	location?: string;

	// Contact
	firstName?: string;
	lastName?: string;
	address?: string;
	phone?: string;
	email?: string;

	linkedCases?: LinkedCase[];

	success?: SearchProblem[];
	error?: HttpErrorResponse;
}

export interface SaveSearchQueryPayload {
	// Details
	savedQueryName: string;
	caseNumber?: string;
	description?: string;
	typeId?: string;
	subTypeId?: string;
	statusId?: string;
	sourceId?: string;
	groupId?: string;
	assigneeId?: string;
	contactId?: string;
	roleId?: string;
	reportedDateBegin?: string;
	reportedDateEnd?: string;
	completedDateBegin?: string;
	completedDateEnd?: string;
	polygon?: string;
	priorityId?: string;
	workOrderNumber?: string;

	// Location
	location?: string;

	// Contact
	firstName?: string;
	lastName?: string;
	address?: string;
	phone?: string;
	email?: string;

	// success?: SearchQuery[];
	error?: HttpErrorResponse;
}

export interface FromProblemLinkedCasePayload {
	caseNo: string;
	application: string;
	notes: string;
	isRelated: boolean;
	problemOneNumber: string;
	problemTwoNumber: string;
	error?: HttpErrorResponse;
}

export interface MessageToastOptions {
	type: 'success' | 'error';
	title: string;
	message: string;
}

export interface LinkedCasePayload {
	application: string;
	notes: string;
	isRelated: boolean;
	problemOneNumber: string;
	problemTwoNumber: string;
}

export interface LinkedCaseProblems {
	problemOne: Problem;
	problemTwoArr?: Problem[];
	linkedCase?: LinkedCase;
}

export interface LinkedCase {
	application: string;
	description: string;
	isRelated: boolean;
	linkCreatedBy: number;
	linkCreationDate: Date;
	notes: string;
	problemLinked_Id: number;
	problemOne: Problem;
	problemTwo?: Problem;
	problemOne_RefId: number;
	problemTwo_RefId?: number;
	problemOneNumber: string;
	problemTwoNumber: string;
	focus?: boolean;
}

export interface LinkedCaseDashboardChildren {
	problemNo: string;
	childProblemNo: string;
	priority: string;
	reportedDate: string;
	problemType_Name: string;
	problemLocation: string;
	problemStatus: string;
	assignedTo: string;
	problemRead: string;
}

export interface ParentChildCases {
	application: string;
	description: string;
	location: string;
	notes: string;
	problemLinked_Id: number;
	problem: Problem;
	problem_RefId: number;
	problemNumber: string;
	relationship: string;
}

export interface LinkedCaseRelated {
	linkPossibility: string;
	parentNumber: string;
	childNumber: string;
	linkedCases: ParentChildCases[];
	searchNumberStatus: string;
}

export interface UpdateLinkedCasePayload {
	linkedCase_Id: number;
	updatedLinkedCase: LinkedCase;
	caseNo: string;
}

export interface UnlinkProblemCasePayload {
	linkedCase_Id: number;
	caseNo: string;
}

export interface ActivityType {
	activityType_Id: number;
	activityType_Name: string;
	isAssignable: boolean;
	systemActivity: boolean;
	active: boolean;
}

export interface ProblemSource {
	problemSource_Id: number;
	problemSource_Name: string;
	active: boolean;
}

export interface ProblemReceived {
	problemReceivedFrom_Id: number;
	problemReceivedFrom_Name: string;
	active: boolean;
}

export interface ProblemStatus {
	problemStatus_Id: number;
	problemStatus_Name: string;
	active: boolean;
}

export interface User {
	user_Id: number;
	firstName: string;
	lastName: string;
	email: string;
	employee_No: number;
	group_RefId: number;
	active: boolean;
	upn: string;
	edocsAlias?: string;
}

export interface Assignee {
	user_Id?: number;
	email: string;
	employee_No?: number;
	group_RefId?: number;
	active: boolean;
	upn?: string;
	edocsAlias?: string;
	role_Id?: number;
	name: string;
	group?: string;
	type: string;
}

export enum CustomAdminFilterModalTargets {
	Groups = 'Groups',
	Roles = 'Roles',
	None = '',
}

export interface AdminUserView {
	user_Id?: Number;
	firstName: string;
	lastName: string;
	email: string;
	active: boolean;
	upn: string;
	groupSelections: Number[];
	groupNamesStr?: string;
	roleSelections: Number[];
	roleNamesStr?: string;
	edocsAlias: string;
}

export enum AdminSections {
	Users = 'users',
	Groups = 'groups',
	Roles = 'roles',
}

export enum AdminModalViewMode {
	Add = 'add',
	Edit = 'edit',
}

export function userFirstNameLastName(user: User): string {
	if (user) {
		return user.firstName + ' ' + user.lastName;
	}

	return 'Unknown User';
}

// export interface Department {
//   department_Id: number;
//   department_Name: string;
//   email: string;
// }

export interface Group {
	group_Id: number;
	name: string;
	email: string;
	active: boolean;
}

export interface Role {
	role_Id: number;
	group_RefId: number;
	name: string;
	email: string;
	active: boolean;
	group?: string;
}

export interface GetMenuOptionPayload {
	success?: MenuItem;
	error?: HttpErrorResponse;
}

export interface ProblemNeighborScope {
	problemNeighborScope: string[];
}

export interface ProblemNeighborPayload {
	problemNo: string;
	success?: string[];
	error?: HttpErrorResponse;
}

export interface GetProblemsPayload {
	problemNo: string;
	success?: Problem[];
	error?: HttpErrorResponse;
}

export interface GetContactsPayload {
	problemNo: string;
	success?: any[];
	error?: HttpErrorResponse;
}

export interface HeartbeatPayload {
	actions: Action[];
	retryCount: number;
	maxRetry: number;
}

export interface ProblemCodeCasePayload {
	caseNumber: string;
	linkedStatus: string;
	permitType?: string;
	success?: any;
	error?: any;
}

export interface ProblemContactsPayload {
	problem_Id: number;
	contacts: ProblemContact[];
	success?: ProblemContact[];
	error?: HttpErrorResponse;
}

export interface AddActivityPayload {
	caseNumber: string;
	activity: Activity;
	success?: ActivityList;
	error?: HttpErrorResponse;
}

export interface AddWorkOrderPayload {
	workOrderTypeRefId: number;
	activityId: number;
	activity: Activity;
	success?: any;
	error?: any;
}

export interface SubmitWorkOrderPayload {
	workOrderTypeRefId: number;
	activity: Activity;
	success?: any;
	error?: any;
}

export interface DeleteWorkOrderPayload {
	activityId: number;
	activity: Activity;
	success?: any;
	error?: any;
}

export interface DeleteEntityPayload {
	id: number;
	problem_Id: number;
	success?: any;
	error?: HttpErrorResponse;
}

export interface ContactType {
	contactType_Id: number;
	contactType_Name: string;
	active: boolean;
}

export interface ProblemAssignment {
	problemAssignment_Id: number;
	problemType_RefId: number;
	group_RefId: number;
	role_RefId: number | null;
	user_RefId: number | null;
	location: string;
	locationType: string;
}

export interface GroupUser {
	groupUser_Id: number;
	group_RefId: number;
	user_RefId: number;
	user?: User;
}

export interface RoleUser {
	roleUser_Id: number;
	role_RefId: number;
	user_RefId: number;
}

export interface WorkOrder {
	workOrder_Id: number;
	workOrderType_RefId: number;
	problem_RefId: string;
	caseNo: string;
	workOrderNumber: string;
	workOrderStatus: string;
	activity_RefId: number;
	timestamp: string;
	createdBy: string;
}

export interface WorkOrderTypeList {
	workOrderType_Id: string;
	workOrderCategory: string;
	workOrderTypeDescription: string;
	type: string;
	standingWOrkOrderNumber: string;
	group_RefId: number;
	problemType_RefId: number;
}

export enum BatchExportProgress {
	Summarizing = 'summarizing',
	SummaryComplete = 'summaryComplete',
	LoadingDetails = 'loadingDetails',
	LoadingHistory = 'loadingHistory',
}

export interface MenuItem {
	currentUser: User;
	allProblemTypeList: ProblemType[];
	allProblemSubTypeList: SelectedProblemSubType[];
	// problemTypeList: ProblemType[];
	problemPriorityList: ProblemPriority[];
	problemSourceList: ProblemSource[];
	problemReceivedFromList: ProblemReceived[];
	// departmentUserList: User[];
	allUsersList: User[];
	problemStatusList: ProblemStatus[];
	activityTypeList: ActivityType[];
	contactTypes: ContactType[];
	phoneTypes: PhoneType[];
	problemGroupList: Group[];
	problemRoleList: Role[];
	problemAssignments: ProblemAssignment[];
	groupUser: GroupUser[];
	roleUser: RoleUser[];
	workOrderTypeList: WorkOrderTypeList[];
}

export interface MenuDictionaries {
	problemTypes: Map<number, ProblemType>;
	allUsers: Map<number, User>;
	groupList: Map<number, Group>;
	sourceList: Map<number, ProblemSource>;
	statusList: Map<number, ProblemStatus>;
	roleList: Map<number, Role>;
}

// export const getAllProblemTypeList = (menu: MenuItem) => menu.allProblemTypeList;
// // export const getProblemTypeList = (menu: MenuItem) => menu.problemTypeList;
export const getProblemSourceList = (menu: MenuItem) => menu.problemSourceList;
export const getProblemReceivedFromList = (menu: MenuItem) => menu.problemReceivedFromList;
export const getProblemPriorityList = (menu: MenuItem) => menu.problemPriorityList;
// // export const getDepartmentUserList = (menu: MenuItem) => menu.departmentUserList;
export const getAllUsersList = (menu: MenuItem) => menu.allUsersList;
export const getProblemAssignmentList = (menu: MenuItem) => menu.problemAssignments;
// export const getProblemStatusList = (menu: MenuItem) => menu.problemStatusList;
// export const getActivityTypeList = (menu: MenuItem) => menu.activityTypeList;
// export const getDepartmentList = (menu: MenuItem) => menu.departmentList;
export const getRoles = (menu: MenuItem) => menu.problemRoleList;
export const getGroupUsers = (menu: MenuItem) => menu.groupUser;
export const getRoleUsers = (menu: MenuItem) => menu.roleUser;

export interface ApplicationState {
	asset: Asset[];
	status: ApplicationStatus;
	dashboardView: DashboardView;
	dashboardTabView: string;
	problems: Problem[];
	recentProblems: Problem[];
	problemsFiltered: Problem[];
	problemDashboard: string[];
	problemNeighbors: string[];
	problemNeighborScope: string[];
	linkedCases: LinkedCase[];
	linkedCasesAddMenu: LinkedCaseRelated; // Table menu within the add link case
	selectedProblem: string;
	problemHistory: History[];
	noPin: boolean;
	windowInnerHeight: number;
	windowInnerWidth: number;
	menu: MenuItem;
	menuDictionaries: MenuDictionaries;
	galleryOptions: NgxGalleryOptions[];
	activities: ActivityList[];
	tasks: TaskList[];
	categorySubType: ProblemCategorySubType[];
	caseStep: number;
	selectedProblemType: ProblemSubType;
	problemSubTypes: SelectedProblemSubType[];
	lastCreatedCaseNo?: LastCreatedCaseNo;
	redirectPath?: string;
	descriptionHistory: History[];
	rapUsers: Map<number, User>;
	problemContacts: ProblemContact[];
	searchCoordinate: SearchCoordinatesPayload | null;
	propertyOwners: ContactDB[];
	searchResults: SearchProblem[];
	savedSearchQueries: SearchQuery[];
	attachments: Attachment[];
	galleryImages: NgxGalleryImage[];
	newCaseMenu: ProblemMenuConfig[];
	noProblems: boolean;
	transferringToCityworks: boolean;
	creatingCodeCase: boolean;
	inProgressActivity: boolean;
	inProgressMyTask: boolean;
	access: boolean;
	scrollToResults: boolean;
	appView: string;
	expandedProblem: string;
	viewLoading: boolean;
	zoneLookupOnly?: boolean;
	arcGisToken: ArcGisToken;
	isNation: string;
	profilePicture: Blob | null;
	cityworksRequestedView: CityWorksRequestViewResponse;
	cityworksTransferMapping: CityworksTransferMapping[];
	isGettingCityworksViewData: boolean;
	admin: AdminPayload;
}

export interface LastCreatedCaseNo {
	caseNo: string;
	department: string;
	user: string;
}

export enum ApplicationStatus {
	Error,
	// Live,
	Legend,
	Layer,
	Table,
	Toolbox,
	Draw,
	Measure,
	Idle,
	Export,
}

export interface UserNotification {
	type: string; // One of: 'success', 'error', 'info', 'warning'
	title: string;
	message: string;
	options: { [id: string]: any };
}

// export interface SearchFromCoordinatesErrorPayload {
//   error: string;
//   locationDescription: string[];
// }

// export interface SearchCoordinatesSuccessPayload {
//   ltopid: number;
//   dblink: string;
//   lat: number;
//   lng: number;
//   searchItem: SearchItem;
// }
export interface SearchCoordinatesErrorPayload {
	error: string;
	searchItem: SearchItem;
}

export interface LookupOwnerPayload {
	ltopid: string;

	success?: ContactDB[];
	error?: HttpErrorResponse;
}

export const initialArcGisToken: ArcGisToken = {
	token: 'expired-token',
	expires: 0,
	ssl: false,
};

export function ArcGisTokenAboutToExpire(arcGisToken: ArcGisToken): boolean {
	return arcGisToken.expires < Date.now() + 5 * 60 * 1000;
}

export interface RenewArcGisTokenPayload {
	delayedActions: Action[];
	success?: ArcGisToken;
	error?: HttpErrorResponse;
}

export interface ArcGisToken {
	token: string;
	expires: number;
	ssl: boolean;
}

export interface CityworksComment {
	comments: string;
	authorName: string;
	dateCreated: Date;
}

export interface CityworksWorkOrder {
	workOrderId: string;
	description: string;
	initiateDate: Date;
	status: string;
	priority: string;
	submitToName: string;
	dateSubmitTo: Date;
	closedByName: string;
	dateWOClosed: Date;
	resolution: string;
	resolutionDescription: string;
	comments: CityworksComment[];
	acctNum: string;
	text1: string;
	text2: string;
}

export interface CityworksImage {
	fileType: string;
	fileName: string;
	fileData: string;
}

export interface CityWorksRequestViewResponse {
	description: string;
	status: string;
	priority: string;
	submitToName: string;
	dateSubmitTo: Date;
	details: string;
	closedByName: string;
	comments: CityworksComment[];
	dateTimeClosed: Date;
	images: CityworksImage[];
	workOrders: CityworksWorkOrder[];
	resolutionDescription: string;
	text6: string;
}

export interface CityworksTransferMapping {
	problemType_RefId: number;
	group_RefId: number;
}

// export interface AppState {
//   mapState: DnvMapState;
//   application: ApplicationState;
// }
const coords: DnvLatLng = { lat: 0, lng: 0 };

export const initialProblem: Problem = {
	problemId: 0,
	problemType_RefId: 0,
	problemPriority_RefId: 1,
	priority: 'Low',
	problemNo: '',
	problemDescription: '',
	reportedDate: new Date(),
	problemStatus: '',
	problemLocation: '',
	problemSource: '',
	problemReceivedFrom: '',
	group_RefId: 0,
	problemGroup: '',
	problemRole: '',
	lastViewedAt: '',
	latLng: coords,
	assignedTo: '',
	lastUpdatedAt: '',
	lastUpdatedBy: 0,
	problemType_Name: '',
	problemRead: '',
	linkedStatus: '',
	lastViewedAtDateTime: null,
};

export const initialApplicationState: ApplicationState = {
	asset: [],
	status: ApplicationStatus.Idle,
	dashboardView: null,

	dashboardTabView: 'top',
	problems: [],
	recentProblems: [],
	problemsFiltered: [],
	problemDashboard: [],
	problemNeighbors: [],
	problemNeighborScope: [],
	linkedCases: [],
	linkedCasesAddMenu: { linkPossibility: null, parentNumber: null, childNumber: null, searchNumberStatus: null, linkedCases: [] },
	selectedProblem: '',
	problemSubTypes: [],
	problemHistory: [],
	descriptionHistory: [],
	noPin: false,
	windowInnerHeight: window.innerHeight,
	windowInnerWidth: window.innerWidth,
	menu: {
		currentUser: null,
		allProblemTypeList: [],
		allProblemSubTypeList: [],
		problemPriorityList: [],
		problemSourceList: [],
		problemReceivedFromList: [],
		allUsersList: [],
		problemStatusList: [],
		activityTypeList: [],
		contactTypes: [],
		phoneTypes: [],
		problemAssignments: [],
		problemGroupList: [],
		problemRoleList: [],
		groupUser: [],
		roleUser: [],
		workOrderTypeList: [],
	},
	menuDictionaries: {
		problemTypes: new Map<number, ProblemType>(),
		allUsers: new Map<number, User>(),
		groupList: new Map<number, Group>(),
		sourceList: new Map<number, ProblemSource>(),
		statusList: new Map<number, ProblemStatus>(),
		roleList: new Map<number, Role>(),
	},
	galleryOptions: [
		{
			width: '100%',
			height: '300px',
			imageArrowsAutoHide: true,
			imageDescription: false,
			previewDescription: true,
			// imageSize: NgxGalleryImageSize.Contain,
			// thumbnailSize: NgxGalleryImageSize.Contain,
			previewSwipe: true,
			thumbnailsAutoHide: true,
			thumbnailsColumns: 3,
		},
		{ breakpoint: 800, width: '100%', height: '300px', thumbnailsColumns: 3 },
		{ breakpoint: 300, width: '100%', height: '200px', thumbnailsColumns: 2 },
	],
	activities: [],
	tasks: [],
	categorySubType: [],
	caseStep: 3,
	selectedProblemType: { problemType_Id: 0, problemType_Name: 'Add a Problem' },
	lastCreatedCaseNo: null,
	rapUsers: new Map<number, User>(),
	problemContacts: [],
	searchCoordinate: null,
	propertyOwners: [],
	searchResults: [],
	savedSearchQueries: [],
	attachments: [],
	galleryImages: [],
	newCaseMenu: [],
	noProblems: true,
	transferringToCityworks: false,
	creatingCodeCase: false,
	inProgressActivity: false,
	inProgressMyTask: false,
	access: true,
	scrollToResults: false,
	appView: 'desktop',
	expandedProblem: '',
	viewLoading: false,
	zoneLookupOnly: false,
	arcGisToken: initialArcGisToken,
	isGettingCityworksViewData: false,
	cityworksRequestedView: {
		description: '',
		status: '',
		priority: '',
		submitToName: '',
		dateSubmitTo: new Date(),
		details: '',
		closedByName: '',
		comments: [],
		dateTimeClosed: new Date(),
		images: [],
		workOrders: [],
		resolutionDescription: '',
		text6: '',
	},
	cityworksTransferMapping: [],
	isNation: '',
	profilePicture: null,
	admin: {
		users: [],
		section: 'users',
		groups: [],
		roles: [],
		isAdmin: false,
		adminADUserList: [],
	},
};

export interface AdminPayload {
	section: string;
	isAdmin: boolean;
	users: AdminUserView[];
	groups: Group[];
	roles: Role[];
	adminADUserList: AdminUserView[];
}
